import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Levels.css";
import { levels, GAME_WIDTH, GROUND_Y, CHAR_WIDTH, CHAR_HEIGHT } from "./levelData";
import { Platform, FallingStar } from "./types";

interface LevelsProps {
  user: any;
}

const GRAVITY = 0.8;
const JUMP_FORCE = -17;
const MOVE_SPEED = 5;
const PLATFORM_HEIGHT = 16;
const PORTAL_WIDTH = 48;
const PORTAL_HEIGHT = 72;
const STAR_SIZE = 24;
const DEFAULT_STAR_SPEED = 4;
const DEFAULT_STAR_SPAWN_RATE = 800;

const Levels: React.FC<LevelsProps> = () => {
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isGrounded, setIsGrounded] = useState(true);
  const [facingRight, setFacingRight] = useState(true);
  const [levelComplete, setLevelComplete] = useState(false);
  const [stars, setStars] = useState<FallingStar[]>([]);

  const keysPressed = useRef<Set<string>>(new Set());
  const gameLoopRef = useRef<number | null>(null);
  const velocityRef = useRef({ x: 0, y: 0 });
  const starIdRef = useRef(0);

  const currentLevel = levels[currentLevelIndex];

  // Initialize level
  useEffect(() => {
    setPosition({
      x: currentLevel.spawnPoint.x,
      y: currentLevel.spawnPoint.y,
    });
    velocityRef.current = { x: 0, y: 0 };
    setIsGrounded(false);
    setLevelComplete(false);
    setStars([]);
    starIdRef.current = 0;
  }, [currentLevelIndex, currentLevel.spawnPoint.x, currentLevel.spawnPoint.y]);

  // Star spawning
  useEffect(() => {
    if (!currentLevel.hasStars || levelComplete) return;

    const spawnRate = currentLevel.starSpawnRate || DEFAULT_STAR_SPAWN_RATE;

    const spawnStar = () => {
      const newStar: FallingStar = {
        id: starIdRef.current++,
        x: Math.random() * (GAME_WIDTH - STAR_SIZE),
        y: -STAR_SIZE,
        speed: currentLevel.starSpeed || DEFAULT_STAR_SPEED,
      };
      setStars(prev => [...prev, newStar]);
    };

    const intervalId = setInterval(spawnStar, spawnRate);
    return () => clearInterval(intervalId);
  }, [currentLevel.hasStars, currentLevel.starSpawnRate, currentLevel.starSpeed, levelComplete]);

  // Check platform collision (landing on top)
  const checkPlatformCollision = useCallback(
    (x: number, y: number, vy: number): { collided: boolean; newY: number } => {
      const playerBottom = y + CHAR_HEIGHT;
      const playerLeft = x;
      const playerRight = x + CHAR_WIDTH;

      for (const platform of currentLevel.platforms) {
        const platTop = platform.y;
        const platLeft = platform.x;
        const platRight = platform.x + platform.width;

        // Check if player is above platform and falling
        if (
          vy >= 0 &&
          playerRight > platLeft &&
          playerLeft < platRight &&
          playerBottom >= platTop &&
          playerBottom <= platTop + PLATFORM_HEIGHT + vy
        ) {
          return { collided: true, newY: platTop - CHAR_HEIGHT };
        }
      }

      return { collided: false, newY: y };
    },
    [currentLevel.platforms]
  );

  // Check portal collision
  const checkPortalCollision = useCallback(
    (x: number, y: number): boolean => {
      const playerCenterX = x + CHAR_WIDTH / 2;
      const playerCenterY = y + CHAR_HEIGHT / 2;

      const portalLeft = currentLevel.portalPosition.x;
      const portalRight = portalLeft + PORTAL_WIDTH;
      const portalTop = currentLevel.portalPosition.y;
      const portalBottom = portalTop + PORTAL_HEIGHT;

      return (
        playerCenterX >= portalLeft &&
        playerCenterX <= portalRight &&
        playerCenterY >= portalTop &&
        playerCenterY <= portalBottom
      );
    },
    [currentLevel.portalPosition]
  );

  // Check star collision
  const checkStarCollision = useCallback(
    (playerX: number, playerY: number, currentStars: FallingStar[]): boolean => {
      for (const star of currentStars) {
        const starRight = star.x + STAR_SIZE;
        const starBottom = star.y + STAR_SIZE;
        const playerRight = playerX + CHAR_WIDTH;
        const playerBottom = playerY + CHAR_HEIGHT;

        if (
          playerX < starRight &&
          playerRight > star.x &&
          playerY < starBottom &&
          playerBottom > star.y
        ) {
          return true;
        }
      }
      return false;
    },
    []
  );

  // Restart level (no prompt)
  const restartLevel = useCallback(() => {
    setPosition({
      x: currentLevel.spawnPoint.x,
      y: currentLevel.spawnPoint.y,
    });
    velocityRef.current = { x: 0, y: 0 };
    setIsGrounded(false);
    setStars([]);
  }, [currentLevel.spawnPoint.x, currentLevel.spawnPoint.y]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      keysPressed.current.add(e.code);

      // Jump on space or up arrow
      if ((e.code === "Space" || e.code === "ArrowUp") && isGrounded && !levelComplete) {
        velocityRef.current.y = JUMP_FORCE;
        setIsGrounded(false);
      }
    },
    [isGrounded, levelComplete]
  );

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    keysPressed.current.delete(e.code);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Game loop
  useEffect(() => {
    if (levelComplete) return;

    const gameLoop = () => {
      // Update stars
      if (currentLevel.hasStars) {
        setStars(prevStars => {
          const updatedStars = prevStars
            .map(star => ({
              ...star,
              y: star.y + star.speed,
            }))
            .filter(star => star.y < GROUND_Y); // Remove stars that hit ground
          return updatedStars;
        });
      }

      setPosition((pos) => {
        let newX = pos.x;
        let newY = pos.y;

        // Horizontal movement
        if (keysPressed.current.has("ArrowLeft")) {
          newX -= MOVE_SPEED;
          setFacingRight(false);
        }
        if (keysPressed.current.has("ArrowRight")) {
          newX += MOVE_SPEED;
          setFacingRight(true);
        }

        // Boundary check
        newX = Math.max(0, Math.min(GAME_WIDTH - CHAR_WIDTH, newX));

        // Apply gravity
        velocityRef.current.y += GRAVITY;
        newY += velocityRef.current.y;

        // Platform collision
        const platformCheck = checkPlatformCollision(newX, newY, velocityRef.current.y);
        if (platformCheck.collided) {
          newY = platformCheck.newY;
          velocityRef.current.y = 0;
          setIsGrounded(true);
        }

        // Ground collision
        const groundLevel = GROUND_Y - CHAR_HEIGHT;
        if (newY >= groundLevel) {
          newY = groundLevel;
          velocityRef.current.y = 0;
          setIsGrounded(true);
        } else if (!platformCheck.collided && velocityRef.current.y > 0) {
          // Falling and not on platform
          setIsGrounded(false);
        }

        // Portal collision
        if (checkPortalCollision(newX, newY)) {
          setLevelComplete(true);
        }

        return { x: newX, y: newY };
      });

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [levelComplete, checkPlatformCollision, checkPortalCollision, currentLevel.hasStars]);

  // Check star collision separately to access current stars state
  useEffect(() => {
    if (!currentLevel.hasStars || levelComplete || stars.length === 0) return;

    if (checkStarCollision(position.x, position.y, stars)) {
      restartLevel();
    }
  }, [position, stars, currentLevel.hasStars, levelComplete, checkStarCollision, restartLevel]);

  const handleNextLevel = () => {
    if (currentLevelIndex < levels.length - 1) {
      setCurrentLevelIndex((i) => i + 1);
    } else {
      // All levels complete - restart from beginning
      setCurrentLevelIndex(0);
    }
  };

  const handleRestartLevel = () => {
    setPosition({
      x: currentLevel.spawnPoint.x,
      y: currentLevel.spawnPoint.y,
    });
    velocityRef.current = { x: 0, y: 0 };
    setIsGrounded(false);
    setLevelComplete(false);
  };

  return (
    <div className="levels-container">
      <div className="level-header">
        <button
          className="level-nav-btn center"
          onClick={() => setCurrentLevelIndex(i => Math.max(0, i - 1))}
          disabled={currentLevelIndex === 0}
        >
          &lt;
        </button>
        <span className="level-name">
          Level {currentLevel.id}: {currentLevel.name}
        </span>
        <button
          className="level-nav-btn center"
          onClick={() => setCurrentLevelIndex(i => Math.min(levels.length - 1, i + 1))}
          disabled={currentLevelIndex === levels.length - 1}
        >
          &gt;
        </button>
      </div>
      <div className="game-area">
        {/* Platforms */}
        {currentLevel.platforms.map((platform: Platform, index: number) => (
          <div
            key={index}
            className="platform"
            style={{
              left: platform.x,
              top: platform.y,
              width: platform.width,
              height: platform.height || PLATFORM_HEIGHT,
            }}
          />
        ))}

        {/* Character */}
        <div
          className={`pixel-character ${facingRight ? "facing-right" : "facing-left"} ${!isGrounded ? "jumping" : ""}`}
          style={{
            left: position.x,
            top: position.y,
          }}
        >
          <div className="character-head"></div>
          <div className="character-body"></div>
          <div className="character-legs">
            <div className="leg left"></div>
            <div className="leg right"></div>
          </div>
        </div>

        {/* Falling Stars */}
        {stars.map(star => (
          <div
            key={star.id}
            className="falling-star"
            style={{
              left: star.x,
              top: star.y,
            }}
          />
        ))}

        {/* Ground */}
        <div className="ground"></div>

        {/* Portal */}
        <div
          className="lvl-complete-portal"
          style={{
            left: currentLevel.portalPosition.x,
            top: currentLevel.portalPosition.y,
          }}
        />

        {/* Level Complete Overlay */}
        {levelComplete && (
          <div className="level-complete-overlay">
            <div className="level-complete-modal">
              <h2>Level Complete!</h2>
              <p>{currentLevel.name}</p>
              <div className="level-complete-buttons">
                <button onClick={handleRestartLevel}>Replay</button>
                <button onClick={handleNextLevel} className="primary">
                  {currentLevelIndex < levels.length - 1 ? "Next Level" : "Play Again"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="controls-hint">
        Arrow keys to move, Space/Up to jump
      </div>
    </div>
  );
};

export default Levels;
